import React, {useContext, useEffect, useMemo, useState} from "react";
import {Alert, Avatar, Box, Snackbar, Stack, Tab, Tabs, TextField, Typography,} from "@mui/material";
import {useMutation, useQuery, useQueryClient} from "react-query";

import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import Spinner from "../../../components/Spinner";
import CustomTabPanel from "../../../components/CustomTabPanel";
import {fetchCoefficients, updateCoefficients,} from "../../../api/setttingsApi";
import {LanguageContext} from "../../../components/LanguageContext";

const COEF_KEYS = ["v", "ax", "bx", "cx", "ay", "by", "cy", "az", "bz", "cz"];
const TREND_KEYS = ["a", "b", "c"];

const a11yProps = (i) => ({
    id: `settings-tab-${i}`,
    "aria-controls": `settings-tabpanel-${i}`,
});

const ParamRow = ({name, label, helper, value, onCommit, disabled}) => {
    const [draft, setDraft] = useState(value ?? "");

    useEffect(() => {
        setDraft(value ?? "");
    }, [value]);

    const handleBlur = () => {
        if (draft !== value) onCommit(name, draft);
    };

    return (
        <Stack
            direction={{xs: "column", sm: "row"}}
            spacing={2}
            alignItems={{xs: "stretch", sm: "center"}}
            sx={(theme) => ({
                py: 2,
                "&:not(:last-of-type)": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                },
            })}
        >
            <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{minWidth: {xs: "auto", sm: 220}}}
            >
                <Avatar
                    sx={(theme) => ({
                        width: 36,
                        height: 36,
                        bgcolor: theme.palette.action.hover,
                        color: "primary.main",
                        fontSize: "0.85rem",
                        fontWeight: 700,
                    })}
                >
                    {name.toUpperCase()}
                </Avatar>
                <Box>
                    <Typography variant="body2" sx={{fontWeight: 600}}>
                        {label}
                    </Typography>
                    {helper && (
                        <Typography variant="caption" color="text.secondary">
                            {helper}
                        </Typography>
                    )}
                </Box>
            </Stack>
            <TextField
                size="small"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.target.blur();
                    }
                }}
                onBlur={handleBlur}
                disabled={disabled}
                fullWidth
                sx={{ml: {sm: "auto"}}}
            />
        </Stack>
    );
};

const SettingsPage = () => {
    const {translations} = useContext(LanguageContext) || {};
    const queryClient = useQueryClient();

    const [tab, setTab] = useState(0);
    const [snack, setSnack] = useState("");

    const {data, isLoading} = useQuery({
        queryKey: ["coefficients"],
        queryFn: () => fetchCoefficients(),
    });

    const valuesByKey = useMemo(() => {
        const map = {};
        (data?.data || []).forEach((c) => {
            map[(c.name || "").toLowerCase()] = c.value;
        });
        return map;
    }, [data]);

    const mutation = useMutation({
        mutationFn: (formData) => updateCoefficients(formData),
        onSuccess: (resp) => {
            setSnack(resp.data.message || "Sačuvano.");
            queryClient.invalidateQueries({queryKey: ["coefficients"]});
        },
    });

    const handleCommit = (name, value) => {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("value", value);
        mutation.mutateAsync(formData).catch(() => {
        });
    };

    const coefLabel = (k) =>
        translations?.[
            `adminSettingsPageParameter${k.toUpperCase()}`
            ] || k.toUpperCase();
    const coefHelper = (k) =>
        translations?.[
            `adminSettingsPageParameter${k.toUpperCase()}Label`
            ];

    return (
        <Box>
            <PageHeader
                icon={<SettingsOutlinedIcon/>}
                eyebrow={translations?.adminSettings || "Podešavanja"}
                title={translations?.adminSettings || "Podešavanja"}
                description={
                    translations?.settingsSubtitle ||
                    "Globalni koeficijenti i trend pragovi koji utiču na ABC-VED i ROP."
                }
            />

            <SectionCard padded={false}>
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    sx={(theme) => ({
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        px: 2,
                    })}
                >
                    <Tab
                        icon={<TuneRoundedIcon fontSize="small"/>}
                        iconPosition="start"
                        label={
                            translations?.adminSettingsPageTabCoefficients ||
                            "Koeficijenti"
                        }
                        sx={{textTransform: "none", minHeight: 56}}
                        {...a11yProps(0)}
                    />
                    <Tab
                        icon={<TrendingUpRoundedIcon fontSize="small"/>}
                        iconPosition="start"
                        label={
                            translations?.adminSettingsPageTabTrends ||
                            "Trendovi"
                        }
                        sx={{textTransform: "none", minHeight: 56}}
                        {...a11yProps(1)}
                    />
                </Tabs>

                <Box sx={{p: {xs: 2, md: 3}}}>
                    {isLoading ? (
                        <Spinner variant="inline"/>
                    ) : (
                        <>
                            <CustomTabPanel value={tab} index={0}>
                                {COEF_KEYS.map((k) => (
                                    <ParamRow
                                        key={k}
                                        name={k}
                                        label={coefLabel(k)}
                                        helper={coefHelper(k)}
                                        value={valuesByKey[k]}
                                        onCommit={handleCommit}
                                    />
                                ))}
                            </CustomTabPanel>
                            <CustomTabPanel value={tab} index={1}>
                                {TREND_KEYS.map((k) => (
                                    <ParamRow
                                        key={k}
                                        name={`trend_${k}`}
                                        label={
                                            translations?.[
                                                `adminSettingsPageTrend${k.toUpperCase()}`
                                                ] || k.toUpperCase()
                                        }
                                        helper={
                                            translations?.[
                                                `adminSettingsPageTrend${k.toUpperCase()}Value`
                                                ]
                                        }
                                        value={valuesByKey[`trend_${k}`] || valuesByKey[k]}
                                        onCommit={handleCommit}
                                        disabled
                                    />
                                ))}
                                <Alert severity="info" sx={{mt: 2}}>
                                    Trend pragovi su trenutno samo za pregled.
                                </Alert>
                            </CustomTabPanel>
                        </>
                    )}
                </Box>
            </SectionCard>

            <Snackbar
                open={Boolean(snack)}
                autoHideDuration={2500}
                onClose={() => setSnack("")}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={() => setSnack("")}
                >
                    {snack}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SettingsPage;